import React, { useEffect, useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import AppleIcon from '@mui/icons-material/Apple';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LaptopMacRoundedIcon from '@mui/icons-material/LaptopMacRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import WindowRoundedIcon from '@mui/icons-material/WindowRounded';
import { detectUserDevicePlatform } from './userPanelDevicePlatform';
import './userPanelMovieDetailModal.css';

const WATCH_PLATFORM_META = {
  windows: { label: 'Windows’ta İzle', icon: WindowRoundedIcon },
  android: { label: 'Android’te İzle', icon: AndroidRoundedIcon },
  ios: { label: 'iOS ile İzle', icon: AppleIcon },
  macos: { label: 'macOS’te İzle', icon: LaptopMacRoundedIcon },
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value).split(',').map((item) => item.trim()).filter(Boolean);
};

function DetailPoster({ poster, name }) {
  const normalizedPoster = String(poster || '').trim();
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => setPosterFailed(false), [normalizedPoster]);

  const source = !posterFailed ? normalizedPoster : '';

  return (
    <div className="tvup-movie-detail-poster" aria-hidden="true">
      {source ? (
        <img
          key={source}
          src={source}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setPosterFailed(true)}
        />
      ) : (
        <b>{String(name || '').charAt(0)}</b>
      )}
      <span>4K</span>
    </div>
  );
}

function UserPanelMovieDetailModal({ open, movie, contentType = 'movie', onClose, onWatch, onOpenComments }) {
  const isSeries = contentType === 'series';
  const watchPlatform = useMemo(
    () => WATCH_PLATFORM_META[detectUserDevicePlatform()] || WATCH_PLATFORM_META.windows,
    [],
  );
  const WatchPlatformIcon = watchPlatform.icon;

  const detail = useMemo(() => {
    const contentLabel = isSeries ? 'Dizi' : 'Film';
    const name = String(movie?.name || movie?.title || contentLabel);
    const poster = movie?.poster || movie?.stream_icon || '';
    const genreFallback = movie?.genre || movie?.category_name || contentLabel;
    const platformName = movie?.platformName || movie?.platform || '';
    const fallbackOverview = isSeries
      ? `${name}, ${platformName ? `${platformName} seçkisinde yer alan ` : ''}${String(genreFallback).toLocaleLowerCase('tr-TR')} türündeki dizilerden biri.`
      : `${name}, Tivigo'nun ${String(genreFallback).toLocaleLowerCase('tr-TR')} seçkisinde yer alıyor.`;

    return {
      name,
      originalTitle: movie?.originalTitle || movie?.original_title || (isSeries ? platformName : ''),
      poster,
      backdrop: movie?.backdrop || movie?.backdropUrl || movie?.backdrop_path || poster,
      rating: movie?.imdbRating || movie?.imdb_rating || movie?.rating || '—',
      year: movie?.year || movie?.release_year || '',
      runtime: movie?.runtime || movie?.duration || '',
      certification: movie?.certification || movie?.ageRating || '',
      commentCount: Math.max(0, Number(movie?.yorum_sayisi ?? movie?.commentCount) || 0),
      overview: movie?.overview
        || movie?.description
        || movie?.plot
        || fallbackOverview,
      director: movie?.director || movie?.director_name || '',
      creator: movie?.creator || movie?.createdBy || movie?.showrunner || '',
      cast: toList(movie?.cast || movie?.actors),
      genres: toList(movie?.genres).length ? toList(movie?.genres) : [genreFallback],
    };
  }, [isSeries, movie]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-movie-detail-dialog"
      aria-labelledby="tvup-content-detail-title"
      aria-describedby="tvup-content-detail-description"
      slotProps={{
        backdrop: { className: 'tvup-movie-detail-backdrop' },
        paper: { id: 'tvup-content-detail-modal', className: 'tvup-movie-detail-modal' },
      }}
    >
      <div className="tvup-movie-detail-background" aria-hidden="true">
        {detail.backdrop && <img src={detail.backdrop} alt="" referrerPolicy="no-referrer" />}
      </div>

      <button
        type="button"
        className="tvup-movie-detail-close"
        aria-label={`${isSeries ? 'Dizi' : 'Film'} detaylarını kapat`}
        onClick={onClose}
      >
        <CloseRoundedIcon />
      </button>

      <div className="tvup-movie-detail-body">
        <DetailPoster poster={detail.poster} name={detail.name} />

        <div className="tvup-movie-detail-copy">
          <h2 id="tvup-content-detail-title">{detail.name}</h2>
          {detail.originalTitle && <p className="tvup-movie-detail-original">{detail.originalTitle}</p>}

          <div className="tvup-movie-detail-meta" aria-label={`${isSeries ? 'Dizi' : 'Film'} bilgileri`}>
            <span className="is-rating"><StarRoundedIcon /> <b>{detail.rating}</b></span>
            {detail.year && <span><CalendarMonthRoundedIcon /> {detail.year}</span>}
            {detail.runtime && <span><AccessTimeRoundedIcon /> {detail.runtime}</span>}
            {detail.certification && <span className="is-certification">{detail.certification}</span>}
          </div>

          <div className="tvup-movie-detail-genres" aria-label={`${isSeries ? 'Dizi' : 'Film'} türleri`}>
            {detail.genres.map((genre) => <span key={genre}>{genre}</span>)}
          </div>

          <p id="tvup-content-detail-description" className="tvup-movie-detail-overview">
            {detail.overview}
          </p>

          {(detail.creator || detail.director || detail.cast.length > 0) && (
            <dl className="tvup-movie-detail-credits">
              {detail.creator && <div><dt>Yaratıcı</dt><dd>{detail.creator}</dd></div>}
              {detail.director && <div><dt>Yönetmen</dt><dd>{detail.director}</dd></div>}
              {detail.cast.length > 0 && <div><dt>Oyuncular</dt><dd>{detail.cast.join(', ')}</dd></div>}
            </dl>
          )}

          <div className="tvup-movie-detail-actions">
            <button type="button" className="is-primary" onClick={() => onWatch?.(movie)}>
              <WatchPlatformIcon />
              {watchPlatform.label}
              <ArrowForwardRoundedIcon />
            </button>
            <button
              type="button"
              className="is-secondary"
              aria-haspopup="dialog"
              aria-controls="tvup-comments-modal"
              onClick={() => onOpenComments?.(movie)}
            >
              <ChatBubbleOutlineRoundedIcon />
              Yorumlar
              <small>{detail.commentCount}</small>
            </button>
            <span><i /> Resmî Tivigo uygulamasında açılır</span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default UserPanelMovieDetailModal;
